/**
 * React Query Hooks
 * Custom hooks for data fetching with React Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { DraftRequestKey } from '../lib/requestIdentity';
import { useToast } from '../components/Toast';

// Dashboard hooks
import * as dashboardApi from '../services/dashboardApi';
import * as studentsApi from '../services/studentsApi';
import * as labsApi from '../services/labsApi';
import * as gradingApi from '../services/gradingApi';
import * as environmentsApi from '../services/environmentsApi';
import * as announcementsApi from '../services/announcementsApi';
import * as cohortsApi from '../services/cohortsApi';
import type { GradeInput, CreateAnnouncementInput } from '../types';

// =====================================================
// QUERY KEYS
// =====================================================

export const queryKeys = {
  dashboard: {
    stats: ['dashboard', 'stats'] as const,
    activity: (limit: number) => ['dashboard', 'activity', limit] as const,
    trends: (days: number) => ['dashboard', 'trends', days] as const,
    labPerformance: ['dashboard', 'labPerformance'] as const,
  },
  students: {
    all: ['students'] as const,
    list: (params: object) => ['students', 'list', params] as const,
    detail: (id: string) => ['students', 'detail', id] as const,
    progress: (id: string) => ['students', 'progress', id] as const,
    environments: (id: string) => ['students', 'environments', id] as const,
  },
  labs: {
    all: ['labs'] as const,
    list: (params: object) => ['labs', 'list', params] as const,
    detail: (id: string) => ['labs', 'detail', id] as const,
    categories: ['labs', 'categories'] as const,
    rubric: (id: string) => ['labs', 'rubric', id] as const,
  },
  grading: {
    all: ['grading'] as const,
    submissions: (params: object) => ['grading', 'submissions', params] as const,
    submission: (id: string) => ['grading', 'submission', id] as const,
    stats: (cohortId?: string) => ['grading', 'stats', cohortId] as const,
  },
  environments: {
    all: ['environments'] as const,
    list: (params: object) => ['environments', 'list', params] as const,
    stats: ['environments', 'stats'] as const,
  },
  announcements: {
    all: ['announcements'] as const,
    list: (params: object) => ['announcements', 'list', params] as const,
    cohorts: ['announcements', 'cohorts'] as const,
    labs: ['announcements', 'labs'] as const,
  },
  cohorts: {
    all: ['cohorts'] as const,
    list: (params: object) => ['cohorts', 'list', params] as const,
    detail: (id: string) => ['cohorts', 'detail', id] as const,
    progress: (id: string) => ['cohorts', 'progress', id] as const,
    students: (id: string) => ['cohorts', 'students', id] as const,
    courses: ['cohorts', 'courses'] as const,
  },
};

// =====================================================
// DASHBOARD HOOKS
// =====================================================

export function useDashboardStats() {
  return useQuery({
    queryKey: queryKeys.dashboard.stats,
    queryFn: dashboardApi.getDashboardStats,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

export function useRecentActivity(limit = 20) {
  return useQuery({
    queryKey: queryKeys.dashboard.activity(limit),
    queryFn: () => dashboardApi.getRecentActivity(limit),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useProgressTrends(days = 14) {
  return useQuery({
    queryKey: queryKeys.dashboard.trends(days),
    queryFn: () => dashboardApi.getProgressTrends(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLabPerformance() {
  return useQuery({
    queryKey: queryKeys.dashboard.labPerformance,
    queryFn: dashboardApi.getLabPerformance,
    staleTime: 5 * 60 * 1000,
  });
}

// =====================================================
// STUDENT HOOKS
// =====================================================

export function useStudents(params: Parameters<typeof studentsApi.getStudents>[0] = {}) {
  return useQuery({
    queryKey: queryKeys.students.list(params),
    queryFn: () => studentsApi.getStudents(params),
    staleTime: 60 * 1000,
  });
}

export function useStudentDetail(studentId: string) {
  return useQuery({
    queryKey: queryKeys.students.detail(studentId),
    queryFn: () => studentsApi.getStudentDetail(studentId),
    enabled: !!studentId,
    staleTime: 60 * 1000,
  });
}

export function useStudentProgress(studentId: string) {
  return useQuery({
    queryKey: queryKeys.students.progress(studentId),
    queryFn: () => studentsApi.getStudentProgress(studentId),
    enabled: !!studentId,
    staleTime: 60 * 1000,
  });
}

export function useStudentEnvironments(studentId: string) {
  return useQuery({
    queryKey: queryKeys.students.environments(studentId),
    queryFn: () => studentsApi.getStudentEnvironments(studentId),
    enabled: !!studentId,
    staleTime: 30 * 1000,
  });
}

export function useSendMessage() {
  const draftRequest = useRef(new DraftRequestKey());
  return useMutation({
    mutationFn: async ({ studentId, message }: { studentId: string; message: string }) => {
      const requestId = draftRequest.current.forPayload(JSON.stringify([studentId, message.trim()]));
      await studentsApi.sendMessageToStudent(studentId, message, requestId);
      draftRequest.current.complete(requestId);
    },
  });
}

// =====================================================
// LAB HOOKS
// =====================================================

export function useLabs(params: Parameters<typeof labsApi.getLabs>[0] = {}) {
  return useQuery({
    queryKey: queryKeys.labs.list(params),
    queryFn: () => labsApi.getLabs(params),
    staleTime: 60 * 1000,
  });
}

export function useLabDetail(labId: string) {
  return useQuery({
    queryKey: queryKeys.labs.detail(labId),
    queryFn: () => labsApi.getLabDetail(labId),
    enabled: !!labId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLabCategories() {
  return useQuery({
    queryKey: queryKeys.labs.categories,
    queryFn: labsApi.getLabCategories,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateLab() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: labsApi.createLab,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.labs.all });
    },
  });
}

export function useUpdateLab() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ labId, data }: { labId: string; data: Parameters<typeof labsApi.updateLab>[1] }) =>
      labsApi.updateLab(labId, data),
    onSuccess: (_, { labId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.labs.detail(labId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.labs.all });
    },
  });
}

export function useDeleteLab() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: labsApi.deleteLab,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.labs.all });
    },
  });
}

export function useToggleLabPublish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ labId, publish }: { labId: string; publish: boolean }) =>
      labsApi.toggleLabPublish(labId, publish),
    onSuccess: (_, { labId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.labs.detail(labId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.labs.all });
    },
  });
}

// =====================================================
// GRADING HOOKS
// =====================================================

export function useSubmissions(params: Parameters<typeof gradingApi.getSubmissions>[0] = {}) {
  return useQuery({
    queryKey: queryKeys.grading.submissions(params),
    queryFn: () => gradingApi.getSubmissions(params),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function usePendingSubmissions(params?: Omit<Parameters<typeof gradingApi.getPendingSubmissions>[0], 'status'>) {
  return useQuery({
    queryKey: queryKeys.grading.submissions({ ...params, status: 'pending' }),
    queryFn: () => gradingApi.getPendingSubmissions(params),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useSubmission(submissionId: string) {
  return useQuery({
    queryKey: queryKeys.grading.submission(submissionId),
    queryFn: () => gradingApi.getSubmission(submissionId),
    enabled: !!submissionId,
    staleTime: 60 * 1000,
  });
}

export function useGradingRubric(labId?: string) {
  return useQuery({
    queryKey: queryKeys.labs.rubric(labId ?? ''),
    queryFn: () => gradingApi.getRubric(labId!),
    enabled: Boolean(labId),
    staleTime: 60 * 1000,
  });
}

export function useGradeSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, data }: { submissionId: string; data: GradeInput }) =>
      gradingApi.gradeSubmission(submissionId, data),
    onSuccess: (_, { submissionId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.grading.submission(submissionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.grading.all });
    },
  });
}

export function useGradingStats(cohortId?: string) {
  return useQuery({
    queryKey: queryKeys.grading.stats(cohortId),
    queryFn: () => gradingApi.getGradingStats(cohortId),
    staleTime: 60 * 1000,
  });
}

export function useAutoGradeAll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: gradingApi.autoGradeAll,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.grading.all });
    },
  });
}

export function useExportGrades() {
  return useMutation({
    mutationFn: gradingApi.exportGrades,
    onSuccess: (blob, params) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `grades-${params.labId || params.cohortId || 'all'}.${params.format || 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
}

// =====================================================
// ENVIRONMENT HOOKS
// =====================================================

export function useEnvironments(params: Parameters<typeof environmentsApi.getEnvironments>[0] = {}) {
  return useQuery({
    queryKey: queryKeys.environments.list(params),
    queryFn: () => environmentsApi.getEnvironments(params),
    staleTime: 15 * 1000, // 15 seconds - more frequent for real-time feel
    refetchInterval: 30 * 1000,
  });
}

export function useEnvironmentStats() {
  return useQuery({
    queryKey: queryKeys.environments.stats,
    queryFn: environmentsApi.getEnvironmentStats,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useStopEnvironment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, environmentId }: { userId: string; environmentId: string }) =>
      environmentsApi.stopEnvironment(userId, environmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.environments.all });
    },
  });
}

export function useStartEnvironment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, environmentId }: { userId: string; environmentId: string }) =>
      environmentsApi.startEnvironment(userId, environmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.environments.all });
    },
  });
}

export function useRestartEnvironment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, environmentId }: { userId: string; environmentId: string }) =>
      environmentsApi.restartEnvironment(userId, environmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.environments.all });
    },
  });
}

export function useDeleteEnvironment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, environmentId }: { userId: string; environmentId: string }) =>
      environmentsApi.deleteEnvironment(userId, environmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.environments.all });
    },
  });
}

export function useEnvironmentLogs(userId: string | null, environmentId: string | null) {
  return useQuery({
    queryKey: ['environments', 'logs', userId, environmentId],
    queryFn: () => environmentsApi.getEnvironmentLogs(userId!, environmentId!),
    enabled: !!userId && !!environmentId,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 15 * 1000, // Auto-refresh logs every 15 seconds
  });
}

// =====================================================
// ANNOUNCEMENT HOOKS
// =====================================================

export function useAnnouncements(params: Parameters<typeof announcementsApi.getAnnouncements>[0] = {}) {
  return useQuery({
    queryKey: queryKeys.announcements.list(params),
    queryFn: () => announcementsApi.getAnnouncements(params),
    staleTime: 60 * 1000,
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAnnouncementInput) => announcementsApi.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    },
  });
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAnnouncementInput> }) =>
      announcementsApi.updateAnnouncement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: announcementsApi.deleteAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    },
  });
}

export function useToggleAnnouncementPin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isPinned }: { id: string; isPinned: boolean }) =>
      announcementsApi.toggleAnnouncementPin(id, isPinned),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    },
  });
}

export function useCohorts() {
  return useQuery({
    queryKey: queryKeys.announcements.cohorts,
    queryFn: announcementsApi.getCohorts,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLabsForAnnouncement() {
  return useQuery({
    queryKey: queryKeys.announcements.labs,
    queryFn: announcementsApi.getLabsForAnnouncement,
    staleTime: 5 * 60 * 1000,
  });
}

// =====================================================
// COHORT HOOKS (instructor-owned cohorts page)
// =====================================================
// Distinct from the announcements `useCohorts` above (which returns a
// reduced shape for the announcement targeting dropdown). These hooks
// power the full Cohorts page + detail view.

export function useInstructorCohorts(filters: cohortsApi.CohortFilters = {}) {
  return useQuery({
    queryKey: queryKeys.cohorts.list(filters),
    queryFn: () => cohortsApi.getCohorts(filters),
    staleTime: 60 * 1000,
  });
}

export function useCohort(id: string) {
  return useQuery({
    queryKey: queryKeys.cohorts.detail(id),
    queryFn: () => cohortsApi.getCohort(id),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCohortProgress(id: string) {
  return useQuery({
    queryKey: queryKeys.cohorts.progress(id),
    queryFn: () => cohortsApi.getCohortProgress(id),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCohortStudents(id: string) {
  return useQuery({
    queryKey: queryKeys.cohorts.students(id),
    queryFn: () => cohortsApi.getCohortStudents(id),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCohortCourses() {
  return useQuery({
    queryKey: queryKeys.cohorts.courses,
    queryFn: cohortsApi.getCohortCourses,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateCohort() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: cohortsApi.CohortInput) => cohortsApi.createCohort(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      toast.showSuccess('Cohort created');
    },
    onError: () => toast.showError('Failed to create cohort'),
  });
}

export function useUpdateCohort() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<cohortsApi.CohortInput> }) =>
      cohortsApi.updateCohort(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(id) });
      toast.showSuccess('Cohort updated');
    },
    onError: () => toast.showError('Failed to update cohort'),
  });
}

export function useArchiveCohort() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => cohortsApi.archiveCohort(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(id) });
      toast.showSuccess('Cohort archived');
    },
    onError: () => toast.showError('Failed to archive cohort'),
  });
}

export function useDeleteCohort() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => cohortsApi.deleteCohort(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      toast.showSuccess('Cohort deleted');
    },
    onError: () => toast.showError('Failed to delete cohort'),
  });
}

export function useEnrollStudents() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ cohortId, studentIds }: { cohortId: string; studentIds: string[] }) =>
      cohortsApi.enrollStudents(cohortId, studentIds),
    onSuccess: (_, { cohortId, studentIds }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.students(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      toast.showSuccess(
        studentIds.length === 1
          ? 'Student enrolled'
          : `${studentIds.length} students enrolled`
      );
    },
    onError: () => toast.showError('Failed to enroll students'),
  });
}

export function useRemoveStudent() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ cohortId, studentId }: { cohortId: string; studentId: string }) =>
      cohortsApi.removeStudent(cohortId, studentId),
    onSuccess: (_, { cohortId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.students(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      toast.showSuccess('Student removed from cohort');
    },
    onError: () => toast.showError('Failed to remove student'),
  });
}

export function useBulkEnroll() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ cohortId, file }: { cohortId: string; file: File }) =>
      cohortsApi.bulkEnrollFromCsv(cohortId, file),
    onSuccess: (result, { cohortId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.detail(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.students(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.all });
      const skipped = result.skipped ? `, ${result.skipped} skipped` : '';
      toast.showSuccess(`Enrolled ${result.enrolled} students${skipped}`);
    },
    onError: () => toast.showError('Bulk enrollment failed'),
  });
}
